import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authAPI } from '../services/api';
import { clearActivityCaches } from '../utils/activityStateStore';

interface User {
  id: string;
  name: string;
  email: string;
  role?: 'masterAdmin' | 'admin' | 'endUser';
  mustChangePassword?: boolean;
  emailVerified?: boolean;
  avatarUrl?: string | null;
}

function normalizeStoredUser(raw: unknown): User | null {
  if (!raw || typeof raw !== 'object') return null;
  const u = raw as Record<string, unknown>;
  const id = u.id ?? u._id;
  if (id == null || u.name == null || u.email == null) return null;
  return {
    id: String(id),
    name: String(u.name),
    email: String(u.email),
    role: u.role as User['role'],
    mustChangePassword: !!u.mustChangePassword,
    emailVerified: !!u.emailVerified,
    avatarUrl: (u.avatarUrl as string | null | undefined) ?? null,
  };
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  updateUser: (updates: Partial<User>) => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    
    if (storedToken && storedUser) {
      try {
        const parsed = normalizeStoredUser(JSON.parse(storedUser));
        if (parsed) {
          setToken(storedToken);
          setUser(parsed);
          localStorage.setItem('user', JSON.stringify(parsed));
        }
      } catch {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email: string, password: string) => {
    const response = await authAPI.login(email, password);
    const { token: newToken, user: newUser } = response.data;
    const normalized = normalizeStoredUser(newUser) || newUser;
    
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(normalized));
    
    setToken(newToken);
    setUser(normalized);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    clearActivityCaches();
    setToken(null);
    setUser(null);
  };

  const updateUser = (updates: Partial<User>) => {
    if (!user) return;
    const next = { ...user, ...updates };
    setUser(next);
    localStorage.setItem('user', JSON.stringify(next));
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, updateUser, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
